/**
 * Permission Helper Functions
 *
 * Centralized permission checking for role-based access control
 *
 * Roles:
 * - owner: Full access to all features (created account, pays subscription)
 * - technician: Limited access (invited by owner)
 * - admin: Platform admin (Pete Green)
 *
 * Access Modes:
 * - office: Full desktop experience (default for technicians)
 * - field: Simplified mobile-first UI (for field technicians)
 */

/**
 * Check if user can invite other users to the team
 * @param {Object} user - User object from AuthContext
 * @returns {boolean}
 */
export const canInviteUsers = (user) => {
  if (!user) return false
  return user.role === 'owner' || user.role === 'admin'
}

/**
 * Check if user can manage team (view team page, activate/deactivate members)
 * @param {Object} user - User object from AuthContext
 * @returns {boolean}
 */
export const canManageTeam = (user) => {
  if (!user) return false
  return user.role === 'owner' || user.role === 'admin'
}

/**
 * Check if user can access settings page
 * @param {Object} user - User object from AuthContext
 * @returns {boolean}
 */
export const canAccessSettings = (user) => {
  if (!user) return false
  return user.role === 'owner' || user.role === 'admin'
}

/**
 * Check if user can manage company settings (name, visibility toggle)
 * @param {Object} user - User object from AuthContext
 * @returns {boolean}
 */
export const canManageSettings = (user) => {
  if (!user) return false
  return user.role === 'owner' || user.role === 'admin'
}

/**
 * Check if user can manage billing and subscription
 * @param {Object} user - User object from AuthContext
 * @returns {boolean}
 */
export const canManageBilling = (user) => {
  if (!user) return false
  return user.role === 'owner' || user.role === 'admin'
}

/**
 * Check if user can view all customers
 * @param {Object} user - User object from AuthContext
 * @returns {boolean}
 */
export const canViewAllCustomers = (user) => {
  if (!user) return false
  // Owners and admins can always view all customers
  if (user.role === 'owner' || user.role === 'admin') return true
  // Technicians in office mode can view all customers
  // Field mode technicians cannot (they only work with assessments)
  return user.access_mode === 'office'
}

/**
 * Check if user can view the full dashboard
 * @param {Object} user - User object from AuthContext
 * @returns {boolean}
 */
export const canViewDashboard = (user) => {
  if (!user) return false
  // Field mode users should be redirected to simplified field dashboard
  return user.access_mode === 'office' || user.role === 'owner' || user.role === 'admin'
}

/**
 * Check if user is in field access mode
 * @param {Object} user - User object from AuthContext
 * @returns {boolean}
 */
export const isFieldMode = (user) => {
  if (!user) return false
  return user.access_mode === 'field'
}

/**
 * Check if user is in office access mode
 * @param {Object} user - User object from AuthContext
 * @returns {boolean}
 */
export const isOfficeMode = (user) => {
  if (!user) return false
  return user.access_mode === 'office' || !user.access_mode // Default to office if not set
}

/**
 * Check if user is an admin
 * @param {Object} user - User object from AuthContext
 * @returns {boolean}
 */
export const isAdmin = (user) => {
  if (!user) return false
  return user.role === 'admin'
}

/**
 * Check if user is an owner
 * @param {Object} user - User object from AuthContext
 * @returns {boolean}
 */
export const isOwner = (user) => {
  if (!user) return false
  return user.role === 'owner'
}

/**
 * Check if user is a technician
 * @param {Object} user - User object from AuthContext
 * @returns {boolean}
 */
export const isTechnician = (user) => {
  if (!user) return false
  return user.role === 'technician'
}

/**
 * Get display label for user's role
 * @param {Object} user - User object from AuthContext
 * @returns {string}
 */
export const getRoleLabel = (user) => {
  if (!user || !user.role) return 'Unknown'

  const roleLabels = {
    owner: 'Owner',
    technician: 'Technician',
    admin: 'Admin'
  }

  return roleLabels[user.role] || 'Unknown'
}

/**
 * Get display label for user's access mode
 * @param {Object} user - User object from AuthContext
 * @returns {string}
 */
export const getAccessModeLabel = (user) => {
  if (!user) return 'Office'

  const modeLabels = {
    office: 'Office',
    field: 'Field'
  }

  return modeLabels[user.access_mode] || 'Office'
}

/**
 * Get icon for user's access mode
 * @param {Object} user - User object from AuthContext
 * @returns {string}
 */
export const getAccessModeIcon = (user) => {
  if (!user) return '💼'

  const modeIcons = {
    office: '💼',
    field: '📱'
  }

  return modeIcons[user.access_mode] || '💼'
}

/**
 * Check if user should be redirected to field dashboard
 * @param {Object} user - User object from AuthContext
 * @returns {boolean}
 */
export const shouldRedirectToFieldDashboard = (user) => {
  if (!user) return false
  // Only field mode technicians get redirected
  return user.role === 'technician' && user.access_mode === 'field'
}
